<template>
  <div class="flex items-center">
    <template v-for="(avatar, index) in avatars" :key="index">
      <img
        v-if="avatar.image"

        :class="classes"
        :src="avatar.image"
      >
      <div
        v-else
        class="pointer-events-none"
        :class="classes"
      >
        <p>{{ avatar.initials }}</p>
      </div>
    </template>

    <template v-if="overflowItems.length">
      <UPopover>
        <div class="ml-2 leading-none text-sm text-gray-500 dark:text-gray-400 cursor-pointer">
          + {{ overflowItems.length }}
        </div>

        <template #content>
          <ul class="text-gray-700 dark:text-gray-200 text-sm p-4">
            <li
              v-for="(avatar, index) in overflowItems"
              :key="index"
              class="flex items-center space-x-3 my-1"
            >
              <img
                v-if="avatar.image"
                :class="classes"
                :src="avatar.image"
              >
              <div
                v-else
                class="pointer-events-none"
                :class="classes"
              >
                <p>{{ avatar.initials }}</p>
              </div>

              <span>{{ avatar.label }}</span>
            </li>
          </ul>
        </template>
      </UPopover>
    </template>
  </div>
</template>

<script lang="ts">
export default { name: 'OAvatar' }
</script>

<script setup lang="ts">
interface Props {
  items: any[]
  size?: string | boolean
  baseClass?: string
  rounded?: string
  border?: boolean
  variant?: string
  flat?: boolean
  outline?: boolean
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  baseClass: 'first:ml-0 border-white flex items-center justify-center flex-shrink-0',
  rounded: 'rounded-full',
  border: true,
  variant: 'default',
  flat: false,
  outline: false,
  limit: 5,
})

// Inline the colours mixin variants method
const { variants } = useColoursMixin(props)

const avatars = computed(() => {
  const images = props.items
    .filter(item => item)
    .map((item) => {
      const image = resolveImage(item.image ?? item.images)
      return {
        image,
        initials: item.initials,
      }
    })

  images.length = Math.min(images.length, props.limit)

  return [...new Set(images)]
})

const overflowItems = computed(() => {
  return props.items
    .filter(item => item)
    .slice(props.limit)
    .map((item) => ({
      image: resolveImage(item.image ?? item.images),
      initials: item.initials,
      label: item.label || item.displayName || item.name || item.initials,
    }))
})

const classes = computed(() => {
  const cls = [
    props.baseClass,
    props.rounded,
  ]

  if (props.size === 'sm') {
    cls.push('w-6 h-6 -ml-2 text-xs')
  } else if (props.size === 'md') {
    cls.push('w-7 h-7 -ml-3 text-xs')
  } else if (props.size === 'lg') {
    cls.push('w-14 h-14 -ml-6 text-base')
  } else if (props.size === 'xl') {
    cls.push('w-18 h-18 -ml-9 text-lg')
  } else if (props.size === '2xl') {
    cls.push('w-24 h-24 -ml-9 text-lg')
  }

  if (props.border) {
    if (props.size === 'sm') {
      cls.push('border')
    } else if (props.size === 'md') {
      cls.push('border')
    } else if (props.size === 'lg') {
      cls.push('border-2')
    } else if (props.size === 'xl') {
      cls.push('border-2')
    }
  }

  return variants(cls)
})

function useColoursMixin(props: any) {
  const defaultClass = 'text-gray-700 bg-gray-200 dark:bg-gray-800 dark:text-white/80 hover:bg-gray-300 dark:border-gray-900'
  const infoClass = 'text-white bg-blue-600 dark:bg-blue-800 border-blue-400 hover:bg-blue-700'
  const successClass = 'text-white bg-green-500 dark:bg-green-700 border-green-400 hover:bg-green-700'
  const dangerClass = 'text-white bg-red-500 dark:bg-red-700 border-red-500 hover:bg-red-600 hover:border-red-600'
  const warningClass = 'text-white bg-orange-500 dark:bg-orange-700 border-orange-500 hover:bg-orange-600 hover:border-orange-600'
  const lightClass = 'text-white bg-gray-100 hover:bg-gray-200 hover:text-gray-700'
  const darkClass = 'text-white bg-gray-700 hover:bg-gray-700'
  const primaryClass = 'text-white bg-primary-600 dark:bg-primary-800 hover:bg-primary-600/85'
  const secondaryClass = 'text-white bg-secondary hover:bg-secondary/85'

  const defaultFlatClass = 'text-gray-600 bg-gray-100 dark:bg-white/5 dark:text-white/80 hover:bg-gray-200 hover:text-gray-700'
  const infoFlatClass = 'bg-blue-200 dark:bg-blue-800/20 text-blue-700 dark:text-blue-500 hover:bg-blue-600 hover:text-white'
  const successFlatClass = 'bg-green-100 dark:bg-green-800/20 text-green-700 dark:text-green-500 hover:bg-green-600 hover:text-white'
  const dangerFlatClass = 'bg-red-200 dark:bg-red-800/20 text-red-700 dark:text-red-500 hover:bg-red-600 hover:text-white'
  const warningFlatClass = 'bg-orange-100 dark:bg-orange-800/20 text-orange-700 dark:text-orange-500 hover:bg-orange-600 hover:text-white'
  const lightFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-200 hover:text-gray-700'
  const darkFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-600 hover:text-white'
  const primaryFlatClass = 'bg-primary-200 dark:bg-primary-800/20 text-primary-700 dark:text-primary-500 hover:bg-primary-600 hover:text-white'

  const defaultOutlineClass = 'border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-white dark:text-gray-300'
  const infoOutlineClass = 'border border-blue-400 dark:border-blue-800 text-blue-600'
  const successOutlineClass = 'border border-green-400 dark:border-green-800 text-green-600'
  const dangerOutlineClass = 'border border-red-400 dark:border-red-800 text-red-600'
  const warningOutlineClass = 'border border-orange-400 dark:border-orange-800 text-orange-500'
  const lightOutlineClass = 'border border-gray-50 dark:border-gray-50 text-gray-50'
  const darkOutlineClass = 'border border-gray-800 dark:border-gray-800 text-gray-800'
  const primaryOutlineClass = 'border border-primary-400 dark:border-primary-800 text-primary-600 dark:text-primary-400'

  function variants(classes: string[]) {
    if (!props.flat && !props.outline) {
      switch (props.variant) {
        case 'info': classes.push(infoClass); break
        case 'danger': classes.push(dangerClass); break
        case 'warning': classes.push(warningClass); break
        case 'success': classes.push(successClass); break
        case 'light': classes.push(lightClass); break
        case 'dark': classes.push(darkClass); break
        case 'primary': classes.push(primaryClass); break
        case 'secondary': classes.push(secondaryClass); break
        default: classes.push(defaultClass); break
      }
    }
    if (props.flat) {
      switch (props.variant) {
        case 'info': classes.push(infoFlatClass); break
        case 'danger': classes.push(dangerFlatClass); break
        case 'warning': classes.push(warningFlatClass); break
        case 'success': classes.push(successFlatClass); break
        case 'light': classes.push(lightFlatClass); break
        case 'dark': classes.push(darkFlatClass); break
        case 'primary': classes.push(primaryFlatClass); break
        default: classes.push(defaultFlatClass); break
      }
    }
    if (props.outline) {
      switch (props.variant) {
        case 'info': classes.push(infoOutlineClass); break
        case 'danger': classes.push(dangerOutlineClass); break
        case 'warning': classes.push(warningOutlineClass); break
        case 'success': classes.push(successOutlineClass); break
        case 'light': classes.push(lightOutlineClass); break
        case 'dark': classes.push(darkOutlineClass); break
        case 'primary': classes.push(primaryOutlineClass); break
        default: classes.push(defaultOutlineClass); break
      }
    }
    return classes
  }

  return { variants }
}

function resolveImage(image: unknown): string | null {
  if (typeof image === 'string' && image.length) {
    return image
  }

  if (image && typeof image === 'object') {
    const source = image as Record<string, unknown>
    const value = source.square ?? source.medium ?? source.large ?? source.url ?? source.src
    if (typeof value === 'string' && value.length) {
      return value
    }
  }

  return null
}
</script>
